import React, { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { STATUS, FLAGS } from "@/lib/constants";

const statusColor = (p) => (p?.controle_nodig ? FLAGS.controle_nodig.color : p?.status === "vrij" ? STATUS.vrij.color : STATUS.bezet.color);

// Typeahead for a free pitch; zone is derived from the chosen pitch
export default function PitchPicker({ value, onPick, pitches, zones, arrival, departure, currentLabel }) {
  const { t } = useLang();
  const [text, setText] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [freeInPeriod, setFreeInPeriod] = useState(null);
  const [idx, setIdx] = useState(0);
  const box = useRef(null);

  useEffect(() => { setText(value || ""); }, [value]);

  useEffect(() => {
    if (!arrival || !departure || departure < arrival) { setFreeInPeriod(null); return; }
    let alive = true;
    api.get(`/availability?from=${arrival}&to=${departure}`).then(({ data }) => {
      if (alive) setFreeInPeriod(new Set(data.zones.flatMap((z) => z.free_labels)));
    }).catch(() => alive && setFreeInPeriod(null));
    return () => { alive = false; };
  }, [arrival, departure]);

  const options = useMemo(() => {
    const q = text.trim().toLowerCase();
    return pitches
      // Datum-beschikbaarheid (freeInPeriod) is leidend: een plaats die nú 'bezet' is door een
      // boeking in een andere periode blijft boekbaar voor een periode waarin ze wél vrij is.
      .filter((p) => p.label === currentLabel || (freeInPeriod ? freeInPeriod.has(p.label) : p.status === "vrij"))
      .filter((p) => !q || p.label.toLowerCase().startsWith(q))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }))
      .slice(0, 60);
  }, [pitches, text, freeInPeriod, currentLabel]);

  const zoneOf = (label) => {
    const p = pitches.find((x) => x.label === label);
    return p ? zones.find((z) => z.code === p.zone) : null;
  };
  const zone = zoneOf(value);
  const chosen = pitches.find((p) => p.label === value);

  const choose = (p) => { onPick(p.label, p.zone); setText(p.label); setOpen(false); };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setIdx((i) => Math.min(options.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter" && open && options[idx]) { e.preventDefault(); choose(options[idx]); }
    else if (e.key === "Tab" || e.key === "Escape") {
      const exact = options.find((p) => p.label.toLowerCase() === text.trim().toLowerCase());
      if (exact) choose(exact); else if (options.length === 1) choose(options[0]);
      setOpen(false);
    }
  };

  const onBlur = () => {
    setTimeout(() => {
      setOpen(false);
      const exact = pitches.find((p) => p.label.toLowerCase() === text.trim().toLowerCase());
      if (exact && options.includes(exact)) choose(exact);
      else if (!exact) { setText(value || ""); }
    }, 120);
  };

  return (
    <div className="relative flex items-center gap-2" ref={box}>
      <Input
        value={text}
        onChange={(e) => { setText(e.target.value.toUpperCase()); setOpen(true); setIdx(0); if (value) onPick("", ""); }}
        onFocus={() => setOpen(true)}
        onBlur={onBlur}
        onKeyDown={onKey}
        placeholder={t("type_pitch")}
        className="font-semibold uppercase"
        autoComplete="off"
        data-testid="reservation-pitch"
      />
      <span
        className="shrink-0 h-9 min-w-[3.5rem] px-2 rounded-md grid place-items-center text-xs font-bold text-white transition-colors"
        style={{ background: value ? statusColor(chosen) : "#cbd5e1" }}
        title={zone?.name || t("zone")}
        data-testid="reservation-zone-badge"
      >
        {zone ? `${zone.code} · ${zone.name}` : t("zone")}
      </span>
      {open && options.length > 0 && (
        <ul className="absolute left-0 top-full mt-1 z-50 w-full max-h-56 overflow-auto cb-scroll rounded-md border border-border bg-card shadow-lg py-1" data-testid="pitch-options">
          {options.map((p, i) => {
            const z = zones.find((x) => x.code === p.zone);
            return (
              <li key={p.id} onMouseDown={() => choose(p)} data-testid={`pitch-option-${p.label}`}
                className={`flex items-center justify-between px-3 py-1.5 text-sm cursor-pointer ${i === idx ? "bg-secondary" : "hover:bg-muted"}`}>
                <span className="font-semibold">{p.label}</span>
                <span className="text-[10px] font-bold text-white rounded px-1.5 py-0.5" style={{ background: statusColor(p) }}>{z?.code} · {z?.name}</span>
              </li>
            );
          })}
        </ul>
      )}
      {open && options.length === 0 && text && (
        <div className="absolute left-0 top-full mt-1 z-50 w-full rounded-md border border-border bg-card shadow-lg px-3 py-2 text-xs text-muted-foreground">{t("no_free_pitch")}</div>
      )}
    </div>
  );
}
